import { useFeatureFlagEnabled } from "@posthog/react";

import { MARKETPLACE_GO_LIVE_ENABLED_FLAG_KEY } from "../lib/feature-flags";
import { readMarketplaceOverride, type Override } from "./useMarketplaceState";

export type ConsumerMarketplaceGate = "loading" | "off" | "on";

/**
 * Plan 435 M1 (mfh-gate-tristate) — "Gate tri-state" (tech spec Concepts).
 *
 * `useConsumerMarketplaceEnabled` collapses PostHog's `undefined` (flag
 * still loading) into `false` via `useMarketplaceState`'s "pre" state, which
 * is the right call for every OTHER marketplace surface (fail closed before
 * the SDK resolves). Home needs to tell "still loading" apart from "resolved
 * closed" so it can render a skeleton instead of flashing the readiness
 * variant — so this hook reads the SAME inputs one level up, before that
 * collapse happens.
 *
 * Inputs (identical to `useConsumerMarketplaceEnabled`):
 *   - `import.meta.env.DEV` → always "on" (local dev, `/preview/*`,
 *     ui-verify have no PostHog key).
 *   - The `?flags=marketplace_go_live_enabled` / `!marketplace_go_live_enabled`
 *     sessionStorage override (shared cache with `useMarketplaceState`) →
 *     "on" / "off".
 *   - The raw `marketplace_go_live_enabled` flag: `true` → "on", `false` →
 *     "off", `undefined` (SDK not yet loaded, or the flag key is missing) →
 *     "loading".
 *
 * Home is the ONLY consumer of this hook (decision: "Home handles the flag
 * tri-state itself"). Every other marketplace surface keeps
 * `useConsumerMarketplaceEnabled`'s boolean.
 */
export function resolveConsumerMarketplaceGate(
  flagEnabled: boolean | undefined,
  override: Override,
  dev: boolean,
): ConsumerMarketplaceGate {
  if (dev) return "on";
  if (override === "force-on") return "on";
  if (override === "force-off") return "off";
  if (flagEnabled === true) return "on";
  if (flagEnabled === false) return "off";
  return "loading";
}

export function useConsumerMarketplaceGate(): ConsumerMarketplaceGate {
  const flagEnabled = useFeatureFlagEnabled(MARKETPLACE_GO_LIVE_ENABLED_FLAG_KEY);
  const override = readMarketplaceOverride();
  return resolveConsumerMarketplaceGate(flagEnabled, override, import.meta.env.DEV);
}
